import { UserDto } from "../modules/Account/AccountLogin/types";
import clientStorage from "./clientStorage";

export interface SsoAccount {
  sessionKey: string;
  name?: string;
  userName?: string;
  email?: string;
  personnelId?: string;
  userRoles?: number[];
  isRoot: boolean;
}

export interface SsoAccountState {
  rootSessionKey: string | null;
  activeSessionKey: string | null;
  accounts: SsoAccount[];
}

const ACCOUNTS_KEY = "sso_accounts";
const LEGACY_ACTIVE_KEY = "secondary_session_key";
const LEGACY_ACTIVE_USER = "secondary_user";

const getSessionKey = () => clientStorage.getItem("session_key");
const setSessionKey = (sessionKey: string) => {
  clientStorage.removeItem("session_key");
  clientStorage.setItem("session_key", sessionKey);
};
const purgeSessionKey = () => clientStorage.removeItem("session_key");

const getUser = () => clientStorage.getItem("user");
const setUser = (user: UserDto) => {
  clientStorage.removeItem("user");
  clientStorage.setItem("user", JSON.stringify(user));
};
const purgeUser = () => clientStorage.removeItem("user");

const parseUser = (raw: string | null): UserDto | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserDto;
  } catch {
    return null;
  }
};

const buildRootAccount = (): SsoAccount | null => {
  const sessionKey = getSessionKey();
  if (!sessionKey) return null;

  const user = parseUser(getUser());
  return {
    sessionKey,
    userName: user?.username,
    email: user?.email,
    userRoles: user?.userRoles || [],
    isRoot: true,
  };
};

const writeState = (state: SsoAccountState) => {
  clientStorage.removeItem(ACCOUNTS_KEY);
  clientStorage.setItem(ACCOUNTS_KEY, JSON.stringify(state));
};

const purgeLegacyKeys = () => {
  clientStorage.removeItem(LEGACY_ACTIVE_KEY);
  clientStorage.removeItem(LEGACY_ACTIVE_USER);
};

const migrateLegacyState = (root: SsoAccount): SsoAccountState => {
  const accounts: SsoAccount[] = [root];
  let activeSessionKey = root.sessionKey;

  const legacyKey = clientStorage.getItem(LEGACY_ACTIVE_KEY);
  if (legacyKey && legacyKey !== root.sessionKey) {
    const legacyUser = parseUser(clientStorage.getItem(LEGACY_ACTIVE_USER));
    accounts.push({
      sessionKey: legacyKey,
      userName: legacyUser?.username,
      email: legacyUser?.email,
      userRoles: legacyUser?.userRoles || [],
      isRoot: false,
    });
    activeSessionKey = legacyKey;
  }

  const state: SsoAccountState = {
    rootSessionKey: root.sessionKey,
    activeSessionKey,
    accounts,
  };
  writeState(state);
  purgeLegacyKeys();
  return state;
};

const readState = (): SsoAccountState => {
  const root = buildRootAccount();
  if (!root)
    return { rootSessionKey: null, activeSessionKey: null, accounts: [] };

  const raw = clientStorage.getItem(ACCOUNTS_KEY);
  if (!raw) return migrateLegacyState(root);

  try {
    const parsed = JSON.parse(raw) as SsoAccountState;

    if (parsed?.rootSessionKey !== root.sessionKey) {
      const fresh: SsoAccountState = {
        rootSessionKey: root.sessionKey,
        activeSessionKey: root.sessionKey,
        accounts: [root],
      };
      writeState(fresh);
      purgeLegacyKeys();
      return fresh;
    }

    const stored = (parsed?.accounts || []).filter((a) => a && !!a.sessionKey);
    if (!stored.length) return migrateLegacyState(root);

    const accounts = stored.map((a) =>
      a.sessionKey === root.sessionKey
        ? { ...a, ...root, isRoot: true }
        : { ...a, isRoot: false }
    );
    if (!accounts.some((a) => a.isRoot)) accounts.unshift(root);

    const activeSessionKey = accounts.some(
      (a) => a.sessionKey === parsed.activeSessionKey
    )
      ? parsed.activeSessionKey
      : root.sessionKey;

    return { rootSessionKey: root.sessionKey, activeSessionKey, accounts };
  } catch {
    return migrateLegacyState(root);
  }
};

const getAccounts = (): SsoAccount[] => readState().accounts;

const getRootAccount = (): SsoAccount | null =>
  readState().accounts.find((a) => a.isRoot) || null;

const getActiveAccount = (): SsoAccount | null => {
  const state = readState();
  return (
    state.accounts.find((a) => a.sessionKey === state.activeSessionKey) ||
    state.accounts.find((a) => a.isRoot) ||
    null
  );
};

const getActiveSessionKey = (): string | null => {
  const rootKey = getSessionKey();
  if (!rootKey) return null;

  const raw = clientStorage.getItem(ACCOUNTS_KEY);
  if (!raw) return clientStorage.getItem(LEGACY_ACTIVE_KEY) || rootKey;

  try {
    const parsed = JSON.parse(raw) as SsoAccountState;
    if (parsed?.rootSessionKey !== rootKey) return rootKey;

    const active = parsed?.activeSessionKey;
    const known = (parsed?.accounts || []).some((a) => a.sessionKey === active);
    return active && known ? active : rootKey;
  } catch {
    return rootKey;
  }
};

const getActiveUser = (): UserDto | null => {
  const account = getActiveAccount();
  if (!account) return null;

  return {
    username: account.userName || "",
    email: account.email || "",
    userRoles: account.userRoles || [],
  };
};

const isActiveAccountRoot = (): boolean => !!getActiveAccount()?.isRoot;

const upsertAccount = (account: SsoAccount, activate: boolean = false) => {
  if (!account || !account.sessionKey) return;

  const state = readState();
  const root = state.accounts.find((a) => a.isRoot) || null;
  const isRoot = account.isRoot || account.sessionKey === root?.sessionKey;

  const existing = state.accounts.find(
    (a) => a.sessionKey === account.sessionKey
  );
  const merged: SsoAccount = { ...existing, ...account, isRoot };

  const rest = state.accounts.filter(
    (a) => a.sessionKey !== account.sessionKey && !(isRoot && a.isRoot)
  );
  const accounts = isRoot ? [merged, ...rest] : [...rest, merged];

  const activeSessionKey = activate
    ? merged.sessionKey
    : accounts.some((a) => a.sessionKey === state.activeSessionKey)
    ? state.activeSessionKey
    : merged.sessionKey;

  writeState({ rootSessionKey: state.rootSessionKey, activeSessionKey, accounts });
};

const activateAccount = (sessionKey: string) => {
  const state = readState();
  if (!state.accounts.some((a) => a.sessionKey === sessionKey)) return;
  writeState({ ...state, activeSessionKey: sessionKey });
};

const activateRootAccount = () => {
  const state = readState();
  const root = state.accounts.find((a) => a.isRoot);
  if (!root) return;
  writeState({ ...state, activeSessionKey: root.sessionKey });
};

const removeAccount = (sessionKey: string) => {
  const state = readState();
  const accounts = state.accounts.filter(
    (a) => a.sessionKey !== sessionKey || a.isRoot
  );
  const root = accounts.find((a) => a.isRoot) || null;
  const activeSessionKey = accounts.some(
    (a) => a.sessionKey === state.activeSessionKey
  )
    ? state.activeSessionKey
    : root?.sessionKey || null;

  writeState({ rootSessionKey: state.rootSessionKey, activeSessionKey, accounts });
};

const keepOnlyRootAccount = () => {
  const state = readState();
  const accounts = state.accounts.filter((a) => a.isRoot);
  writeState({
    rootSessionKey: state.rootSessionKey,
    activeSessionKey: accounts[0]?.sessionKey || null,
    accounts,
  });
};

const resetAccounts = (root: SsoAccount) => {
  writeState({
    rootSessionKey: root.sessionKey,
    activeSessionKey: root.sessionKey,
    accounts: [root],
  });
  purgeLegacyKeys();
};

const purgeAccounts = () => {
  clientStorage.removeItem(ACCOUNTS_KEY);
  purgeLegacyKeys();
};

const purgeAllSessions = () => {
  purgeSessionKey();
  purgeUser();
  purgeAccounts();
};

export {
  activateAccount,
  activateRootAccount,
  getAccounts,
  getActiveAccount,
  getActiveSessionKey,
  getActiveUser,
  getRootAccount,
  getSessionKey,
  getUser,
  isActiveAccountRoot,
  keepOnlyRootAccount,
  purgeAccounts,
  purgeAllSessions,
  purgeSessionKey,
  purgeUser,
  removeAccount,
  resetAccounts,
  setSessionKey,
  setUser,
  upsertAccount,
};
